import React, { useRef, useState } from 'react';
import { App, Alert, Button, Card, Modal, Space, Spin, Typography } from 'antd';
import { CameraOutlined, FolderOpenOutlined } from '@ant-design/icons';
import QRScannerModal from '../facturacion/QRScannerModal';
import { useResponsive } from '../../hooks/useResponsive';
import {
  useAnalyzeExpenseCaptureMutation,
  useGetAutocapturaNoticeQuery,
  useMarkAutocapturaNoticeSeenMutation,
} from '../../services/expensesApi';
import type { ExpenseCaptureResultDto } from '../../types/expense';

const { Text, Paragraph } = Typography;

interface ExpenseCaptureFlowProps {
  restaurantId: string;
  open: boolean;
  onClose: () => void;
  /** El análisis terminó (con o sin lectura). Abre el form precargado. */
  onCaptured: (result: ExpenseCaptureResultDto) => void;
  /** "Mejor capturo a mano": abre el form normal, vacío. */
  onManualInstead: () => void;
}

type Step = 'notice' | 'choose' | 'scanning' | 'analyzing';

/**
 * Flujo de autocaptura: aviso de primer uso, elección de entrada, y análisis.
 *
 * Las dos entradas producen **un solo artefacto** y ninguna manda datos de QR por
 * separado: subir un archivo manda ese archivo, y escanear manda el frame. El servidor
 * decodifica el QR de los bytes que recibe, siempre.
 */
const ExpenseCaptureFlow: React.FC<ExpenseCaptureFlowProps> = ({
  restaurantId,
  open,
  onClose,
  onCaptured,
  onManualInstead,
}) => {
  // App.useApp() y no el import estático: el `message` importado directo de 'antd' no
  // toma el contexto del ConfigProvider y no hace nada.
  const { message, modal } = App.useApp();
  // Este es EL flujo móvil del módulo: el empleado está parado junto al ticket con el
  // celular. La geometría del modal en ≤768px la fija App.css para toda la app (pantalla
  // completa, cuerpo con scroll, pie visible); aquí sólo se apila el pie. Ver finanzas.css.
  const { isMobile } = useResponsive();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('choose');

  const { data: notice, isLoading: loadingNotice } = useGetAutocapturaNoticeQuery(
    { restaurantId },
    { skip: !open },
  );
  const [markNoticeSeen] = useMarkAutocapturaNoticeSeenMutation();
  const [analyze] = useAnalyzeExpenseCaptureMutation();

  const needsNotice = !loadingNotice && notice?.seen === false;
  const currentStep: Step = needsNotice && step !== 'analyzing' ? 'notice' : step;

  const reset = () => setStep('choose');

  const handleClose = () => {
    reset();
    onClose();
  };

  const acceptNotice = async () => {
    try {
      await markNoticeSeen({ restaurantId }).unwrap();
    } catch {
      // Que no se pueda registrar el aviso no debe bloquear la captura: el usuario YA lo
      // leyó, que es el punto. Lo único que se pierde es no volver a mostrarlo, y volver
      // a mostrarlo es el fallo benigno de los dos posibles.
    }
    setStep('choose');
  };

  const runAnalysis = async (file: File | Blob, fileName: string) => {
    setStep('analyzing');
    try {
      const result = await analyze({ restaurantId, file, fileName }).unwrap();
      reset();
      onCaptured(result);
    } catch (error: unknown) {
      reset();

      const status = (error as { status?: number })?.status;
      const serverMessage = (error as { data?: { message?: string } })?.data?.message;

      if (status === 507) {
        // Tope de almacenamiento: no se guardó nada, y el mensaje manda al flujo que sí
        // funciona en vez de dejar al usuario atorado.
        modal.warning({
          title: 'Demasiadas capturas pendientes',
          content: serverMessage,
          okText: 'Usar + Nuevo gasto',
          onOk: onManualInstead,
        });
        return;
      }

      message.error(serverMessage ?? 'No se pudo procesar la foto. Intenta de nuevo.');
    }
  };

  const handleFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';   // permite volver a elegir el mismo archivo
    if (file) {
      void runAnalysis(file, file.name);
    }
  };

  // ── Aviso de primer uso ──────────────────────────────────────────────────

  if (open && currentStep === 'notice') {
    return (
      <Modal
        open
        title="Cómo funciona la lectura automática"
        onCancel={handleClose}
        maskClosable={false}
        footer={
          <div className="fin-modal-footer">
            <Button onClick={onManualInstead} block={isMobile}>
              Mejor capturo a mano
            </Button>
            <Button type="primary" onClick={() => void acceptNotice()} block={isMobile}>
              Entendido, continuar
            </Button>
          </div>
        }
      >
        <Paragraph>
          Para leer el comprobante, <Text strong>la foto se envía a un servicio externo de
          inteligencia artificial</Text>. Se recomprime y se le quita la ubicación antes de
          salir, y no se usa para entrenar modelos.
        </Paragraph>
        <Paragraph>
          El sistema <Text strong>no verifica</Text> que lo que lea sea correcto: siempre vas
          a poder revisar y corregir los datos antes de guardar.
        </Paragraph>
      </Modal>
    );
  }

  // ── Cámara ───────────────────────────────────────────────────────────────

  if (open && currentStep === 'scanning') {
    return (
      <QRScannerModal
        visible
        onClose={reset}
        copy={{
          cameraHint:
            'Encuadra el comprobante completo. Si tiene código QR, la foto se toma sola; '
            + 'si no, usa el botón.',
        }}
        // SÓLO el frame. El texto del QR que el navegador leyó no sale de ese componente.
        onFrameCaptured={(frame) => void runAnalysis(frame, 'comprobante.jpg')}
      />
    );
  }

  // ── Elección de entrada / análisis en curso ──────────────────────────────

  return (
    <Modal
      open={open}
      title="Capturar de un comprobante"
      onCancel={handleClose}
      maskClosable={currentStep !== 'analyzing'}
      closable={currentStep !== 'analyzing'}
      footer={null}
    >
      {currentStep === 'analyzing' ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Leyendo el comprobante…</Paragraph>
          <Text type="secondary">La foto ya se está guardando.</Text>
        </div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Toma la foto y el sistema precarga los datos"
            description="Siempre vas a poder revisar y corregir antes de guardar."
          />

          <Card hoverable onClick={() => setStep('scanning')} style={{ cursor: 'pointer' }}>
            <Space>
              <CameraOutlined style={{ fontSize: 28 }} />
              <div>
                <div><Text strong>Escanear con la cámara</Text></div>
                <Text type="secondary">Ideal en el celular, junto al ticket</Text>
              </div>
            </Space>
          </Card>

          <Card
            hoverable
            onClick={() => fileInputRef.current?.click()}
            style={{ cursor: 'pointer' }}
          >
            <Space>
              <FolderOpenOutlined style={{ fontSize: 28 }} />
              <div>
                <div><Text strong>Subir una foto</Text></div>
                <Text type="secondary">Desde la galería o el equipo</Text>
              </div>
            </Space>
          </Card>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleFilePicked}
          />

          <Button type="link" onClick={onManualInstead} style={{ paddingLeft: 0 }}>
            Mejor capturo a mano
          </Button>
        </Space>
      )}
    </Modal>
  );
};

export default ExpenseCaptureFlow;
